import { useMemo } from "react";
import { useGetSensitivityMeasures } from "./useDashboardQueries";
import { SensitivityMeasuresByPortfolio } from "../../domain/entities/DashboardEntity";

export const useSensitivityMeasuresData = () => {
    const {
        data: sensitivityMeasuresData,
        isLoading: sensitivityMeasuresIsLoading,
        refetch: sensitivityMeasuresRefetch,
    } = useGetSensitivityMeasures();

    const sensitivityMeasures = useMemo(
        () => sensitivityMeasuresData?.data || [],
        [sensitivityMeasuresData]
    );

    const aggregatedByPortfolio = useMemo(() => {
        const map: Record<string, SensitivityMeasuresByPortfolio & { _weightSum: number }> = {};

        for (const item of sensitivityMeasures) {
            const portfolioKey = item.portfolio;
            const weight = item.percentage ?? 0;

            if (!map[portfolioKey]) {
                map[portfolioKey] = {
                    portfolio: portfolioKey,
                    localCurrencyValuation: 0,
                    duration: 0,
                    modifiedDuration: 0,
                    convexity: 0,
                    DV01: 0,
                    _weightSum: 0,
                };
            }

            const entry = map[portfolioKey];

            entry.localCurrencyValuation += item.localCurrencyValuation || 0;
            entry.duration += (item.duration || 0) * weight;
            entry.modifiedDuration += (item.modifiedDuration || 0) * weight;
            entry.convexity += (item.convexity || 0) * weight;
            entry.DV01 += (item.DV01 || 0) * weight;
            entry._weightSum += weight;
        }

        return Object.values(map).map(({ _weightSum, ...rest }) => ({
            ...rest,
            duration: rest.duration,
            modifiedDuration: rest.modifiedDuration,
            convexity: rest.convexity,
            DV01: rest.DV01,
        }));
    }, [sensitivityMeasures]);

    return {
        sensitivityMeasures,
        aggregatedByPortfolio,
        sensitivityMeasuresRefetch,
        isLoading: sensitivityMeasuresIsLoading,
    };
};
